// NodeIntellicaChat.tsx
import { debounce } from 'lodash';
import { Send } from 'lucide-react';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Textarea, Button } from '@/components/ui';
import { createAICommand, AICommandType, AICommandStatus } from '@/utils/aiCommandUtils';
import styles from '~/styles/NodeIntellica.module.css';
import { ChatMessage, NodeIntellicaData } from '../NodeIntellica';
import NodeIntellicaInteractive from '../intellica-interactive/NodeIntellicaInteractive';

interface NodeIntellicaChatProps {
  id: string;
  data: NodeIntellicaData;
  syncNodeData: (newData: Partial<NodeIntellicaData>) => void;
  addNodes: (nodes: any[]) => void;
  addEdges: (edges: any[]) => void;
  callbackMap: Record<string, (response: any) => void>;
}

const NodeIntellicaChat: React.FC<NodeIntellicaChatProps> = ({ id, data, syncNodeData, addNodes, addEdges, callbackMap }) => {
  const [messages, setMessages] = useState<ChatMessage[]>(data.messages || []);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<string>('Initializing'); // Initialize phase
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastProcessedResponseRef = useRef<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const pendingSyncRef = useRef<Partial<NodeIntellicaData> | null>(null);

  const batchedSyncNodeData = useCallback((newData: Partial<NodeIntellicaData>) => {
    pendingSyncRef.current = { ...pendingSyncRef.current, ...newData };
    setTimeout(() => {
      if (pendingSyncRef.current) {
        syncNodeData(pendingSyncRef.current);
        pendingSyncRef.current = null;
      }
    }, 0);
  }, [syncNodeData, id]);

  const handleAIResponse = useCallback((response: string | string[]) => {
    const content = Array.isArray(response) ? response.join(', ') : response;
    const newMessage: ChatMessage = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
    batchedSyncNodeData({ messages: [...messages, newMessage] });
    sendSysagentRequest('process_start', content);
  }, [batchedSyncNodeData, messages]);

  const sendSysagentRequest = useCallback(
    (command: string, response: string) => {
      if (isProcessing) {
        console.log(`[NodeIntellicaChat] Already processing, ignoring request: ${response.substring(0, 50)}...`);
        return;
      }
      
      console.log(`[NodeIntellicaChat] Sending request to processor: ${response.substring(0, 50)}...`);
      setIsProcessing(true);
      
      // Use the createAICommand utility function to ensure the command format matches what the processor expects
      console.log(`[NodeIntellicaChat] Using AICommandType.INTELLICA_PROCESS: ${AICommandType.INTELLICA_PROCESS}`);
      
      const documentRequest = createAICommand(
        AICommandType.INTELLICA_PROCESS,
        AICommandStatus.STARTED,
        {
          document: 'intellica-goal-analysis',
          response,
          [id]: { response },
        }
      );
      
      console.log(`[NodeIntellicaChat] Document request created:`, documentRequest);
      syncNodeData({ documentRequest });
      console.log(`[NodeIntellicaChat] syncNodeData called with documentRequest`);
    },
    [syncNodeData, isProcessing, id]
  );

  const reconstructedMessages = useMemo(() => {
    if (!data.messages) return [];
    return data.messages.map((msg) => ({
      ...msg,
      id: msg.id || `${msg.role}-${new Date(msg.timestamp).getTime()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
      interactive: msg.interactive ? {
        ...msg.interactive,
        callback: callbackMap[msg.interactive.callbackType || 'ai'] || handleAIResponse,
      } : undefined,
    }));
  }, [data.messages, callbackMap, handleAIResponse]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (JSON.stringify(messages) !== JSON.stringify(reconstructedMessages)) {
        setMessages(reconstructedMessages);
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [reconstructedMessages, messages]);

  useEffect(() => {
    if (hasInitialized || data.messages?.length || messages.length) return;

    const initialMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: 'How can I assist you today?',
      timestamp: new Date(),
      interactive: {
        type: 'tree',
        options: [
          {
            label: 'Create something',
            value: 'create',
            description: 'Create a new document, diagram, or node.',
            subOptions: [
              {
                label: 'Document',
                value: 'create_document',
                description: 'Create a structured document.',
                subOptions: [
                  {
                    label: 'Technical Doc',
                    value: 'create_technical_doc',
                    description: 'A detailed technical document.',
                    subOptions: [
                      { label: 'Use Case Document', value: 'create_use_case_doc', description: 'A document detailing how a system is used to achieve specific goals.' },
                      { label: 'API Usage Guidelines', value: 'create_api_guidelines', description: 'A document outlining best practices for using APIs.' },
                      { label: 'Architecture Overview', value: 'create_architecture_overview', description: 'A document providing a high-level overview of a system\'s architecture.' },
                    ],
                  },
                ],
              },
            ],
          },
        ],
        callback: handleAIResponse,
        callbackType: 'ai',
      },
    };
    setMessages([initialMessage]);
    batchedSyncNodeData({ messages: [initialMessage] });
    setHasInitialized(true);
  }, [data.messages, handleAIResponse, batchedSyncNodeData, hasInitialized]);

  useEffect(() => {
    if (!data.documentRequest) {
      console.log(`[NodeIntellicaChat] No documentRequest, setting isProcessing to false`);
      setIsProcessing(false);
      return;
    }

    const sysagentResponse = data.documentRequest['sysagent-response'];
    const responseContent = data.documentRequest.response;
    const responseKey = `${responseContent}-${data.documentRequest.timestamp || Date.now()}`;

    console.log(`[NodeIntellicaChat] Received documentRequest:`, {
      'sysagent-cmd': data.documentRequest['sysagent-cmd'],
      'sysagent-response': sysagentResponse,
      responseContentLength: responseContent?.length || 0,
      timestamp: data.documentRequest.timestamp
    });

    if (lastProcessedResponseRef.current === responseKey) {
      console.log(`[NodeIntellicaChat] Skipping duplicate response with key: ${responseKey}`);
      return;
    }
    
    console.log(`[NodeIntellicaChat] Processing new response with key: ${responseKey}`);
    lastProcessedResponseRef.current = responseKey;

    if (sysagentResponse === 'NodeIntellica-Process-Finished') {
      console.log(`[NodeIntellicaChat] Processing complete response`);
      setIsProcessing(false);
      
      const jsonMatch = responseContent.match(/```json\s*([\s\S]*?)\s*```/);
      console.log(`[NodeIntellicaChat] JSON match found: ${!!jsonMatch}`);
      
      let jsonResponse;
      if (jsonMatch) {
        try {
          jsonResponse = JSON.parse(jsonMatch[1]);
          console.log(`[NodeIntellicaChat] Successfully parsed JSON response with phase: ${jsonResponse.phase}`);
        } catch (error) {
          console.error(`[NodeIntellicaChat] Error parsing JSON response:`, error);
          jsonResponse = {
            phase: 'error',
            analysis: `Error parsing response: ${error.message}`,
            confidence_score: 0,
            intent_score: 0,
            objectives_score: 0,
            actions: [],
            options: [],
          };
        }
      } else {
        console.log(`[NodeIntellicaChat] No JSON found in response, using default values`);
        jsonResponse = {
          phase: 'learning',
          analysis: responseContent || 'No response from AI',
          confidence_score: 0,
          intent_score: 0,
          objectives_score: 0,
          actions: [],
          options: [],
        };
      }

      console.log(`[NodeIntellicaChat] Setting current phase to: ${jsonResponse.phase || 'learning'}`);
      setCurrentPhase(jsonResponse.phase || 'learning'); // Update phase dynamically
      const newMessage = {
        id: `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: jsonResponse.analysis,
        timestamp: new Date(),
        interactive: jsonResponse.options.length ? {
          type: 'tree',
          options: jsonResponse.options.map((opt: any) => ({
            label: opt.label,
            value: opt.value,
            description: opt.description,
            subOptions: opt.subOptions,
          })),
          callback: handleAIResponse,
          callbackType: 'ai',
          multiSelect: true,
        } : undefined,
      };

      setMessages((prev) => {
        const updated = [...prev.filter(m => m.id !== newMessage.id), newMessage];
        batchedSyncNodeData({
          messages: updated,
          confidenceScore: jsonResponse.confidence_score,
          intentScore: jsonResponse.intent_score,
          objectivesScore: jsonResponse.objectives_score,
          documentRequest: null,
        });
        return updated;
      });
    }
  }, [data.documentRequest, batchedSyncNodeData, handleAIResponse]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleMessageSubmit = () => {
    if (!currentMessage.trim()) return;
    const newMessage: ChatMessage = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content: currentMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
    batchedSyncNodeData({ messages: [...messages, newMessage] });
    setCurrentMessage('');
    sendSysagentRequest('process_start', currentMessage);
  };

  return (
    <div className={styles.chatContent}>
      <div className={styles.phaseDisplay}>Phase: {currentPhase}</div> {/* Add phase display */}
      <div className={styles.messagesContainer}>
        {messages.map((msg) => (
          <div key={msg.id} className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.assistantMessage}`}>
            <div className={styles.messageContent}>{msg.content}</div>
            {msg.interactive && <NodeIntellicaInteractive message={msg} />}
            <div className={styles.messageTime}>{new Date(msg.timestamp).toLocaleTimeString()}</div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className={styles.inputContainer}>
        <Textarea
          value={currentMessage}
          onChange={(e) => setCurrentMessage(e.target.value)}
          rows={2}
          className="resize-none w-full"
          placeholder="Type your message..."
          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleMessageSubmit())}
        />
        <Button onClick={handleMessageSubmit} disabled={isProcessing || !currentMessage.trim()}>
          <Send className="mr-2 h-4 w-4" /> Send
        </Button>
      </div>
    </div>
  );
};

export default NodeIntellicaChat;